import { PureComponent } from 'react';
import loadable from '@loadable/component';
import track from 'react-tracking';
import ReportSection from '@components/ReportSection';
import ReportSubSection from '@components/ReportSubSection';
import ReportSectionHeader from '@components/ReportSectionHeader';
import { toSignificant } from '@/utilities/formatting';
import {
  PORTFOLIO_STATIC_COMPANY,
  PORTFOLIO_VOL_vs_MARKET,
  PORTFOLIO_HISTORICAL_VALUE,
  PORTFOLIO_CONTRIBUTORS_TO_RETURNS,
} from '@/constants/feedbackSections';
import { PORTFOLIO_DOWNLOAD_RETURNS_CONTRIBUTOR } from '@/constants/downloadSections';
import { getPortfolioInfographicDownloadUrl } from '@utilities/urlGenerator';
import type { ComputedPortfolioHolding } from '@/components/DeprecatedPortfolio/redux/interface';
import DeferRenderUntilIntersecting from '@components/DeferRenderUntilIntersecting';
const NoData = loadable(() => import('@components/NoData'), {
  resolveComponent: (component) => component.NoData,
});
const HoldingsReturnsChart = loadable(() => import('../HoldingsReturnsChart'), {
  ssr: false,
  resolveComponent: (component) => component.HoldingsReturnsChart,
});
const VolatilityVsMarketChart = loadable(
  () => import('../VolatilityVsMarketChart'),
  {
    ssr: false,
    resolveComponent: (component) => component.VolatilityVsMarketChart,
  }
);
const HistoricalPortfolioValueChart = loadable(
  () => import('@components/charts/HistoricalPortfolioValueChart'),
  { ssr: false }
);
const ChartActionsContainer = loadable(
  () => import('@components/ChartActions'),
  {
    ssr: false,
    resolveComponent: (component) => component.ChartActionsContainer,
  }
);
export interface ReturnsProps {
  isWatchlist: boolean;
  sectionNumber?: number;
  portfolioId: string;
  lastUpdated: number;
  beta?: number;
  holdings?: ComputedPortfolioHolding[];
  portfolioName: string;
  secretCode?: string;
  currencyISO: string;
  currencySymbol?: string;
  chartData: { name: string; data: number[][] }[] | null;
  hasHoldingsInDifferentCurrencies: boolean;
}

@track({
  type: 'track',
})
class Returns extends PureComponent<ReturnsProps> {
  @track((props, state, [chartName, legend, toggleStatus]) => ({
    legend: legend,
    subject: 'chart',
    modifier: chartName,
    action: 'click',
    isVisible: toggleStatus,
  }))
  handleChartClick = () => {
    return true;
  };

  render() {
    const { handleChartClick } = this;
    const {
      portfolioId,
      secretCode,
      sectionNumber,
      beta,
      holdings,
      portfolioName,
      currencySymbol,
      currencyISO,
      chartData,
      isWatchlist,
      lastUpdated,
      hasHoldingsInDifferentCurrencies,
    } = this.props;

    const downloadContributorsToReturnUrl = getPortfolioInfographicDownloadUrl(
      portfolioId,
      PORTFOLIO_DOWNLOAD_RETURNS_CONTRIBUTOR,
      secretCode || ''
    );

    return (
      <ReportSection sectionNumber={sectionNumber}>
        <ReportSectionHeader
          title="Returns"
          sectionDescription="Portfolio volatility and the main contributors to returns"
          keyMetric={beta ? toSignificant(beta, 3) : ''}
          keyMetricTitle="Portfolio Beta / Volatility"
        />
        <ReportSubSection
          title="Portfolio Volatility vs Market"
          trackingProps={{ subject: 'chart' }}
          body={
            holdings && holdings.length > 0 && beta !== null ? (
              <DeferRenderUntilIntersecting preRenderHeight={135}>
                {() => (
                  <>
                    <VolatilityVsMarketChart
                      holdings={holdings}
                      portfolioBeta={beta}
                      portfolioName={portfolioName}
                    />
                    <ChartActionsContainer
                      companyName={PORTFOLIO_STATIC_COMPANY}
                      feedbackLabel={PORTFOLIO_VOL_vs_MARKET}
                    />
                  </>
                )}
              </DeferRenderUntilIntersecting>
            ) : (
              <NoData />
            )
          }
        />
        <hr />
        {!hasHoldingsInDifferentCurrencies && (
          <>
            <ReportSubSection
              title="Historical Portfolio Value"
              trackingProps={{ subject: 'chart' }}
              body={
                isWatchlist === false &&
                hasHoldingsInDifferentCurrencies === false &&
                chartData !== null &&
                chartData.length > 0 ? (
                  <DeferRenderUntilIntersecting preRenderHeight={350}>
                    {() => (
                      <>
                        <HistoricalPortfolioValueChart
                          currencyISO={currencyISO}
                          currencySymbol={currencySymbol}
                          chartData={chartData}
                          analyticsHandler={handleChartClick}
                          portfolioId={portfolioId}
                          lastUpdated={lastUpdated}
                        />
                        <ChartActionsContainer
                          companyName={PORTFOLIO_STATIC_COMPANY}
                          feedbackLabel={PORTFOLIO_HISTORICAL_VALUE}
                        />
                      </>
                    )}
                  </DeferRenderUntilIntersecting>
                ) : (
                  <NoData />
                )
              }
            />
            <hr />
          </>
        )}
        <ReportSubSection
          title="Contributors to Returns"
          trackingProps={{ subject: 'chart' }}
          body={
            holdings &&
            holdings.length > 0 &&
            currencySymbol !== null &&
            typeof currencySymbol !== 'undefined' ? (
              <DeferRenderUntilIntersecting preRenderHeight={400}>
                {() => (
                  <>
                    <HoldingsReturnsChart
                      holdings={holdings}
                      portfolioName={portfolioName}
                      currencySymbol={currencySymbol}
                      portfolioId={portfolioId}
                      lastUpdated={lastUpdated}
                    />
                    <ChartActionsContainer
                      companyName={PORTFOLIO_STATIC_COMPANY}
                      feedbackLabel={PORTFOLIO_CONTRIBUTORS_TO_RETURNS}
                      exportDarkUrl={downloadContributorsToReturnUrl}
                    />
                  </>
                )}
              </DeferRenderUntilIntersecting>
            ) : (
              <NoData />
            )
          }
        />
      </ReportSection>
    );
  }
}

export default Returns;
